// FactoryScene (IMPLEMENTATION §2.1, §2.6 layer model, §2.9): the board. Floor tiles (floor inside the
// expansion mask, rock outside), chutes, buildings, items, stall icons, the tap highlight, board FX and the
// Gallery FX layer, each on its depth band (render/layers.ts). Views are pooled and reconciled with the sim
// state every frame; only chute carries are interpolated (RenderSync); cosmetic events start the M10 board
// FX (render/juice/boardFx.ts) on the FX clock. The scene's `update` hands performance.now() to the game loop
// (Phaser's dt is ignored); the loop ticks the sim and then calls `frame(alpha)`.
// Frame order: FX clock → item views reconcile (views of items that left are kept as `gone` for this frame)
// → the queued events start their effects → tweens / particles step → camera (+ optional shake) → buildings
// and items drawn with their anims → FX sprites + particles drawn → input glows.

import { frameId } from '../../art/catalog.ts';
import type { Cell } from '../../layout/computeLayout.ts';
import type { SimDefs } from '../../sim/defs.ts';
import type { SimEvent } from '../../sim/events.ts';
import type { FxConfig } from '../fxConfig.ts';
import { BoardFx, type BoardFxStats, type FxViews } from '../juice/boardFx.ts';
import { FxClock } from '../juice/clock.ts';
import type { JuiceCfg } from '../juice/config.ts';
import type { GeoCfg } from '../juice/geometry.ts';
import { stallPulse } from '../juice/idle.ts';
import type { ShotGate } from '../juice/shotGate.ts';
import { DEPTH } from '../layers.ts';
import { type InputOverlay, NO_OVERLAY, type RenderSource } from '../Renderer.ts';
import type { BoardCamera } from './camera.ts';
import type { FrameIndex } from './frames.ts';
import { countObjects, imageState, type PScene, type SceneHooks } from './phaserCompat.ts';
import { type Spr, SprPool } from './Spr.ts';

export type { BoardFxStats };

import { BuildingViews } from './views/BuildingViews.ts';
import { FxLayer } from './views/Fx.ts';
import { ItemViews } from './views/ItemViews.ts';

/** The motion settings the board FX read at every event batch (fx/motion.ts, Low quality). */
export interface FxMotion {
  reduceMotion(): boolean;
  cameraShake(): boolean;
  lowQuality(): boolean;
}

export interface FactoryDeps {
  cellWu: number;
  fx: FxConfig;
  /** M10: the juice table, the overlay geometry and the motion settings. */
  juice: JuiceCfg;
  geo: GeoCfg;
  motion: FxMotion;
  /** The FX clock's largest step (ui.json render.frameClampMs). */
  maxStepMs: number;
  /** The visible-shot decision shared with the DOM FX (main.ts); default: the board's own. */
  shots?: ShotGate;
  camera: BoardCamera;
  frames(): FrameIndex;
  /** Muzzle socket centroid of a gun frame (cell units), from the bake's catalog specs. */
  muzzle(frame: string): { x: number; y: number } | null;
  source(): RenderSource | null;
  /** Called from `update` with performance.now() (the game loop). */
  onFrame(nowMs: number): void;
  /** After create(): the scene is ready for frames. */
  onReady(): void;
}

export interface FactoryHandle {
  hooks: SceneHooks;
  scene(): PScene | null;
  frame(alpha: number, nowMs: number): void;
  queue(evs: readonly SimEvent[]): void;
  rebuild(): void;
  highlight(cell: Cell | null): void;
  highlighted(): Cell | null;
  /** M6 input overlay (glows, hidden dragged entity, lifted selection). */
  overlay(o: InputOverlay): void;
  count(kind: string): number;
  worldPos(id: number): { x: number; y: number } | null;
  /**
   * M10: arms an FX-clock freeze `ms` after the next frame (null resumes; render/juice/clock.ts); the camera
   * stops when the FX clock does. While frozen nothing finishes: `__gm.idle()` resolves only after
   * `freezeAt(null)`.
   */
  freezeAt(ms: number | null): void;
  busy(): boolean;
  /** CSS px per cell (every relayout): CSS-px minimums of board sprites (the stall badge), px juice values. */
  setCellCss(px: number): void;
  /**
   * The world cells the viewport shows (every relayout, inclusive): rock fills the ones outside the world grid,
   * so a wide or tall screen (desktop) shows the mine round the column instead of the bare page.
   */
  setView(view: CellSpan): void;
  fxStats(): BoardFxStats;
  fxReset(): void;
}

/** An inclusive span of world cells. */
export interface CellSpan {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** World cells that became floor with expansion `id` (inside it, outside the previous mask). */
export function revealedCells(d: SimDefs, id: string): { x: number; y: number }[] {
  const list = d.board.expansions;
  const i = list.findIndex((e) => e.id === id);
  const m = list[i];
  if (i <= 0 || m === undefined) return [];
  const p = list[i - 1];
  const out: { x: number; y: number }[] = [];
  for (let y = m.y0; y <= m.y1; y++) {
    for (let x = m.x0; x <= m.x1; x++) {
      const before = p !== undefined && x >= p.x0 && x <= p.x1 && y >= p.y0 && y <= p.y1;
      if (!before) out.push({ x, y });
    }
  }
  return out;
}

/** The board FX's deterministic seed (same bursts → same particles on every run: screenshots). */
const FX_SEED = 0x10;

export function createFactoryScene(deps: FactoryDeps): FactoryHandle {
  const wu = deps.cellWu;
  const fx = deps.fx;
  let sc: PScene | null = null;
  let pool: SprPool | null = null;
  let items: ItemViews | null = null;
  let blds: BuildingViews | null = null;
  let layer: FxLayer | null = null;
  const tiles: Spr[] = [];
  let tilesFor = '';
  let view: CellSpan | null = null;
  let hl: Spr | null = null;
  let hlCell: Cell | null = null;
  let pending: SimEvent[] = [];
  let rebuildNext = false;
  let overlay: InputOverlay = NO_OVERLAY;
  let cellCss = 0;
  const glows: Spr[] = [];
  const clock = new FxClock(deps.maxStepMs);
  const board = new BoardFx({
    cfg: deps.juice,
    geo: deps.geo,
    wu,
    seed: FX_SEED,
    byAmmoTier: fx.byAmmoTier,
    spawnScale: fx.spawnScale,
    insertEndScale: fx.insertEndScale,
    cellCss: () => cellCss,
    reduceMotion: deps.motion.reduceMotion,
    cameraShake: deps.motion.cameraShake,
    lowQuality: deps.motion.lowQuality,
    ...(deps.shots ? { shots: deps.shots } : {}),
  });

  const retile = (src: RenderSource): void => {
    if (!pool) return;
    const s = src.state();
    const d = src.defs();
    const cols = d.board.cols;
    const rows = d.board.rows;
    const m = d.board.expansions[s.bounds];
    // the world grid plus the rock skirt the viewport shows round it
    const x0 = Math.min(0, view?.x0 ?? 0);
    const y0 = Math.min(0, view?.y0 ?? 0);
    const x1 = Math.max(cols - 1, view?.x1 ?? 0);
    const y1 = Math.max(rows - 1, view?.y1 ?? 0);
    const key = `${cols}x${rows}:${m ? `${m.x0},${m.y0},${m.x1},${m.y1}` : 'none'}:${x0},${y0},${x1},${y1}`;
    if (key === tilesFor) return;
    tilesFor = key;
    const n = (x1 - x0 + 1) * (y1 - y0 + 1);
    while (tiles.length > n) pool.release(tiles.pop() as Spr);
    while (tiles.length < n) tiles.push(pool.acquire(frameId.rock, DEPTH.floor));
    let i = 0;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const t = tiles[i++] as Spr;
        const inside = m !== undefined && x >= m.x0 && x <= m.x1 && y >= m.y0 && y <= m.y1;
        pool.frame(
          t,
          !inside ? frameId.rock : (x + y) % 2 === 0 ? frameId.floor : frameId.floorAlt,
        );
        t.place((x + 0.5) * wu, (y + 0.5) * wu, wu, wu);
      }
    }
  };

  const applyHighlight = (): void => {
    if (!hl) return;
    if (hlCell) {
      hl.show(true);
      hl.place((hlCell.x + 0.5) * wu, (hlCell.y + 0.5) * wu, wu, wu);
    } else {
      hl.show(false);
    }
  };

  /** Input glows: one pooled ring per glow, tint-filled by kind; merge / select pulse (not in ?test). */
  const syncGlows = (t: number): void => {
    if (!pool) return;
    const want = overlay.glows;
    while (glows.length > want.length) {
      const g = glows.pop();
      if (g) pool.release(g);
    }
    while (glows.length < want.length) glows.push(pool.acquire(frameId.highlight, DEPTH.highlight));
    const pulse = stallPulse(t, fx.glowPulseHz, fx.stallPulseMin, fx.instant);
    want.forEach((g, i) => {
      const s = glows[i] as Spr;
      s.place((g.x + 0.5) * wu, (g.y + 0.5) * wu, wu, wu);
      s.setTint(fx.glow[g.kind], true);
      const pulsing = g.kind === 'merge' || g.kind === 'select';
      const base =
        g.kind === 'floor' ? fx.glowFloorAlpha : g.kind === 'home' ? fx.glowHomeAlpha : 1;
      s.alpha(pulsing ? pulse : base);
    });
  };

  const hooks: SceneHooks = {
    key: 'factory',
    active: false,
    create(scene) {
      sc = scene;
      const frames = deps.frames();
      pool = new SprPool(scene, frames);
      items = new ItemViews(pool, wu);
      blds = new BuildingViews(pool, frames, wu, {
        fx,
        geo: deps.geo,
        machine: deps.juice.ev.machine,
        byAmmoTier: fx.byAmmoTier,
        galleryDryMs: deps.juice.ev.galleryDry.afterMs,
      });
      blds.setCellCss(cellCss);
      layer = new FxLayer(pool);
      hl = pool.acquire(frameId.highlight, DEPTH.highlight);
      applyHighlight();
      deps.onReady();
    },
    update() {
      deps.onFrame(performance.now());
    },
  };

  const handle: FactoryHandle = {
    hooks,
    scene: () => sc,
    frame(alpha, nowMs) {
      const src = deps.source();
      if (!sc || !items || !blds || !layer || !src) return;
      // zero-duration mode (?test): the FX clock never runs (nothing idles, nothing animates)
      const t = fx.instant ? 0 : clock.advance(nowMs);
      const idleT = fx.instant ? 0 : clock.idle;
      // the camera (an expansion tween) stops with the FX clock, not at the freezeAt call
      deps.camera.freeze(clock.frozen);
      if (rebuildNext) {
        rebuildNext = false;
        pending = [];
        board.clear();
        layer.clear();
        items.clear();
        blds.clear();
        tilesFor = '';
      }
      const s = src.state();
      const d = src.defs();
      retile(src);
      blds.setHidden(overlay.hidden);
      items.setOverlay(
        overlay.hidden,
        overlay.lifted?.id ?? null,
        overlay.lifted ? overlay.lifted.cells * wu : 0,
      );
      items.reconcile(s, d, alpha);
      if (pending.length > 0) {
        const evs = pending;
        pending = [];
        const bv = blds;
        const iv = items;
        const views: FxViews = {
          item: (id) => iv.fx(id),
          gone: (id) => iv.gone(id),
          bld: (bid) => bv.fxOf(s, bid),
          goneBld: (bid) => bv.gone(bid),
          revealed: (id) => revealedCells(d, id),
          muzzle: (tier) =>
            deps.muzzle(frameId.gunMount(tier)) ?? deps.muzzle(frameId.item('gun', tier)),
          hasFrame: (id) => deps.frames().has(id),
        };
        board.handle(evs, views, t);
      }
      board.step(t);
      deps.camera.step(sc, nowMs, board.shakeOffset(t));
      blds.sync(s, d, idleT, { still: fx.instant, rm: deps.motion.reduceMotion() });
      items.draw();
      layer.draw(board);
      syncGlows(idleT);
      items.endFrame();
    },
    queue(evs) {
      if (fx.instant || evs.length === 0) return; // zero-duration mode: the views already show the result
      for (const e of evs) pending.push(e);
      // a burst larger than the queue is cosmetic backlog: keep the newest
      if (pending.length > fx.eventQueueMax)
        pending = pending.slice(pending.length - fx.eventQueueMax);
    },
    rebuild() {
      rebuildNext = true;
    },
    highlight(cell) {
      hlCell = cell;
      applyHighlight();
    },
    overlay(o) {
      overlay = o;
    },
    highlighted() {
      if (!hl) return hlCell;
      if (!hl.visible) return null;
      const p = imageState(hl.img);
      return { x: Math.floor(p.x / wu), y: Math.floor(p.y / wu) };
    },
    count(kind) {
      switch (kind) {
        case 'item':
          return items?.count ?? 0;
        case 'carry':
          return items?.carryCount ?? 0;
        case 'building':
          return blds?.count ?? 0;
        case 'stall':
          return blds?.stallCount ?? 0;
        case 'galleryDry':
          return blds?.dryCount ?? 0;
        case 'fx':
          return layer?.count ?? 0;
        case 'galleryFx':
          return layer?.galleryCount ?? 0;
        case 'particles':
          return board.particles.alive;
        case 'tweens':
          return board.tweens.active;
        case 'glow':
          return glows.length;
        case 'strayCrop':
          return pool?.strayCrops((f) => f === frameId.barFill) ?? 0;
        case 'missing':
          return pool ? deps.frames().missed() : 0;
        default:
          return sc ? countObjects(sc, kind) : 0;
      }
    },
    worldPos(id) {
      return items?.worldPos(id) ?? blds?.worldPos(id) ?? null;
    },
    freezeAt(ms) {
      clock.freezeAt(ms);
      if (ms === null) deps.camera.freeze(false);
    },
    busy() {
      return board.busy() || deps.camera.moving();
    },
    setCellCss(px) {
      cellCss = px;
      blds?.setCellCss(px);
    },
    setView(v) {
      view = v;
    },
    fxStats() {
      return {
        ...board.stats(),
        cameraTweens: deps.camera.tweens,
        clockMs: clock.now,
        frozen: clock.frozen,
        instant: fx.instant,
      };
    },
    fxReset() {
      board.resetPeaks();
    },
  };
  return handle;
}
